/*!
 * iLive Chapters — Phase 3.1 animation orchestration
 *
 * Owns:
 *   - hero cross-fade (image A -> image B on scroll)
 *   - per-section fade+rise reveals as elements enter viewport
 *
 * Depends on:
 *   - GSAP 3.12.x and ScrollTrigger, loaded BEFORE this script in theme.liquid
 *   - markup hooks already in Phase 2: .hero-alt__img--a/.hero-alt__img--b,
 *     .shop-cat__card, .era-sel__card, .feat-chap__card etc.
 *
 * Gated on:
 *   - prefers-reduced-motion: skips all ScrollTriggers, jumps to final state
 *   - settings.enable_animations: theme.liquid only emits this script if the
 *     theme setting is on (handled outside this file)
 *
 * Tunables: see CONFIG below — adjust here, refresh, observe.
 */
(function () {
  'use strict';

  // ── Tunable knobs ────────────────────────────────────────────────
  var CONFIG = {
    hero: {
      end: '30% top',  // legacy non-pinned cross-fade end (mobile fallback)
      scrub: 0.4,
      pinDistance: '+=120%',  // desktop pinned timeline scroll length
      textParallaxY: -80,     // text rises this much during pin
      kenBurnsScale: 1.06     // image B scales 1.00 -> this
    },
    featChap: {
      pinDistance: '+=100%',
      quoteCharStaggerMs: 70  // pull-quote drama: slower than headlines
    },
    reveal: {
      duration: 0.4,
      y: 12,
      ease: 'power2.out',
      stagger: 0.04,             // tighter stagger between children
      eraCardStagger: 0.1,       // era cards get a slightly slower stagger
      start: 'top 85%'           // fire when trigger top is 85% down the viewport
    },
    splitText: {
      charStaggerMs: 30,
      duration: 0.4,
      y: 18,
      ease: 'power3.out'
    },
    parallax: {
      eraPlatformY: -40,
      archiveTeaserBgY: 60
    },
    magnetic: {
      ctaMaxPx: 6,
      eraCardMaxPx: 10,
      hitBoxPx: 80,
      tweenDuration: 0.5,
      tweenEase: 'power2.out'
    },
    breathing: {
      distance: 2,
      cycleSec: 4
    }
  };

  // ── Section reveals ──────────────────────────────────────────────
  // A small declarative table of selectors. Each entry can specify a
  // trigger (when the animation fires) separate from the targets
  // (what animates). Keeps the orchestration readable.
  //
  // Hoisted up here (Phase 6.5 hotfix): when GSAP is already loaded,
  // ready() resolves synchronously, so initSectionReveals() runs BEFORE
  // any `var` declaration further down in this IIFE. Declaring REVEAL_SPECS
  // up here ensures it's initialized when init runs.
  var REVEAL_SPECS = [
    // Phase 6.5: shop-cat handled by directional stagger (data-stagger-dir="ltr")
    // Phase 6.5: era-sel heading + intro handled by split-text (data-ilive-split on heading)
    { selector: '.era-sel__card',     trigger: '.era-sel__grid',          stagger: CONFIG.reveal.eraCardStagger },
    // Phase 6.5: feat-chap entire section now driven by initFeatChapTimeline (pin desktop, scroll-in mobile)
    // Phase 6.5: feat-prod heading handled by split-text, grid handled by directional stagger (data-stagger-dir="center")
    { selector: '.feat-prod__eyebrow', trigger: '.feat-prod__inner',      stagger: 0 },
    // Phase 6.5: brand-story headline split-text, body uses clip-reveal — eyebrow stays in REVEAL_SPECS
    { selector: '.brand-story__eyebrow', trigger: '.brand-story',         stagger: 0 },
    // Phase 6.5: arch-teaser headline split-text, bg parallax — eyebrow+sub+cta stay in REVEAL_SPECS
    { selector: '.arch-teaser__eyebrow, .arch-teaser__sub, .arch-teaser__cta', trigger: '.arch-teaser', stagger: 0.06 },
    // Phase 6.5: trust badges now use directional stagger via data-stagger-dir="ltr"
    { selector: '.arch-hero__content',  trigger: '.arch-hero',            stagger: 0 },
    { selector: '.arch-spread',         trigger: '.arch-spreads',         stagger: 0.12 },
    { selector: '.arch-footer-cta__content', trigger: '.arch-footer-cta', stagger: 0 },
    { selector: '.main-coll-header__inner', trigger: '.main-coll-header', stagger: 0 },
    { selector: '.main-coll-grid__cell',    trigger: '.main-coll-grid',   stagger: 0.06 },
    { selector: '.main-prod__info',          trigger: '.main-prod',         stagger: 0 },
    { selector: '.prod-from-chapter__inner', trigger: '.prod-from-chapter', stagger: 0 },
    { selector: '.rel-prod__cell',           trigger: '.rel-prod',          stagger: 0.06 },
    { selector: '.mkt-hero__content',        trigger: '.mkt-hero',          stagger: 0 },
    { selector: '.mkt-content__inner',       trigger: '.mkt-content',       stagger: 0 },
    { selector: '.mkt-cta__inner',           trigger: '.mkt-cta',           stagger: 0 },
    { selector: '.contact-form__intro',      trigger: '.contact-form',      stagger: 0 },
    { selector: '.contact-form__form',       trigger: '.contact-form',      stagger: 0.1 },
    { selector: '.not-found__content',       trigger: '.not-found',         stagger: 0 }
  ];

  // Wait for GSAP + ScrollTrigger to be loaded
  function ready(cb) {
    if (window.gsap && window.ScrollTrigger) return cb();
    var tries = 0;
    var t = setInterval(function () {
      if (window.gsap && window.ScrollTrigger) { clearInterval(t); cb(); return; }
      if (++tries > 100) { clearInterval(t); console.warn('[iLive-animations] GSAP not loaded after 5s'); }
    }, 50);
  }

  ready(function init() {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      // Phase 6.6: hero is now a single static image. No A/B swap to handle.
      // Just ensure split-text targets keep their original text intact and skip the wrap pass.
      Array.prototype.slice.call(document.querySelectorAll('[data-ilive-split]')).forEach(function (el) {
        el.dataset.iliveSplitRevealDone = 'true';
      });
      return; // No ScrollTriggers registered; everything renders in final state by default
    }

    initHeroOnLoadReveal();
    initCinematicIntro();
    initFeatChapTimeline();
    initSectionReveals();
    initSplitTextReveals();
    initParallaxLayers();
    initMagneticElements();
    initDirectionalStagger();
    initBreathingCenterCard();
    initClipReveals();

    // Live-preview support: when Patrick edits a section in the theme editor,
    // Shopify swaps the section's HTML and fires shopify:section:load. We kill
    // any ScrollTriggers inside the affected section and re-register reveals
    // so animations keep working in Customize.
    document.addEventListener('shopify:section:load', function (e) {
      ScrollTrigger.getAll()
        .filter(function (t) { return e.target.contains(t.trigger); })
        .forEach(function (t) { t.kill(); });
      // Re-register reveals on the new section content
      initRevealsInside(e.target);
      initSplitTextReveals(e.target);
      // If the swapped section is the hero, re-register the pin timeline too
      if (e.target.querySelector && e.target.querySelector('.hero-alt')) {
        initHeroOnLoadReveal();
        initCinematicIntro();
      }
      // If the swapped section is featured-chapter, re-register that timeline too
      if (e.target.querySelector && e.target.querySelector('.feat-chap')) {
        initFeatChapTimeline();
      }
      // Re-register parallax + magnetic + breathing + clip + directional stagger globally
      // (helpers are idempotent enough — they re-scan the DOM and skip already-initialized items)
      initParallaxLayers();
      initMagneticElements();
      initBreathingCenterCard();
      initClipReveals();
      initDirectionalStagger();
    });
  });

  // ── Split-text helper ────────────────────────────────────────────
  // Wraps every character of an element's text in <span class="ilive-char">.
  // Idempotent: skips elements already split. Adds aria-label to preserve
  // screen reader pronunciation; spans are aria-hidden.
  function splitChars(el) {
    if (!el || el.dataset.iliveSplitDone === 'true') {
      return Array.prototype.slice.call(el.querySelectorAll('.ilive-char'));
    }
    // Phase 6.13: honor literal <br> tags in source — split into segments
    // first, then word-wrap each segment. <br> tags become real <br> elements
    // that force a line break between the surrounding word-spans.
    var rawHTML = el.innerHTML;
    var segments = rawHTML.split(/<br\s*\/?>/i);
    var ariaLabel = segments.join(' ').replace(/\s+/g, ' ').trim();
    el.setAttribute('aria-label', ariaLabel);
    el.innerHTML = '';
    var spans = [];
    for (var s = 0; s < segments.length; s++) {
      // Decode HTML entities (e.g. &#39; for apostrophe) by reading textContent
      // through a temp div, so the segment string is plain text.
      var tmp = document.createElement('div');
      tmp.innerHTML = segments[s];
      var segText = tmp.textContent;
      var words = segText.split(/(\s+)/);
      for (var w = 0; w < words.length; w++) {
        var token = words[w];
        if (token === '') continue;
        if (/^\s+$/.test(token)) {
          var space = document.createElement('span');
          space.className = 'ilive-char ilive-char--space';
          space.setAttribute('aria-hidden', 'true');
          space.innerHTML = '&nbsp;';
          el.appendChild(space);
          spans.push(space);
          continue;
        }
        var word = document.createElement('span');
        word.className = 'ilive-word';
        word.style.display = 'inline-block';
        word.style.whiteSpace = 'nowrap';
        for (var i = 0; i < token.length; i++) {
          var ch = token.charAt(i);
          var span = document.createElement('span');
          span.className = 'ilive-char';
          span.setAttribute('aria-hidden', 'true');
          span.textContent = ch;
          span.style.display = 'inline-block';
          word.appendChild(span);
          spans.push(span);
        }
        el.appendChild(word);
      }
      // Insert a real <br> between segments (not after the last one)
      if (s < segments.length - 1) {
        el.appendChild(document.createElement('br'));
      }
    }
    el.dataset.iliveSplitDone = 'true';
    return spans;
  }

  // ── Section reveals with split-text ──────────────────────────────
  // Finds [data-ilive-split] elements, splits their text, animates each
  // character in with stagger on scroll-in. Once per element.
  function initSplitTextReveals(root) {
    var scope = root || document;
    var targets = Array.prototype.slice.call(scope.querySelectorAll('[data-ilive-split]'))
      .filter(function(el) { return !el.classList.contains('hero-alt__headline'); });
    targets.forEach(function (el) {
      if (el.dataset.iliveSplitRevealDone === 'true') return;
      var chars = splitChars(el);
      if (chars.length === 0) return;
      gsap.set(chars, { opacity: 0, y: CONFIG.splitText.y });
      gsap.to(chars, {
        opacity: 1,
        y: 0,
        duration: CONFIG.splitText.duration,
        ease: CONFIG.splitText.ease,
        stagger: CONFIG.splitText.charStaggerMs / 1000,
        scrollTrigger: {
          trigger: el,
          start: 'top 90%',
          once: true
        }
      });
      el.dataset.iliveSplitRevealDone = 'true';
    });
  }

  // ── Hero on-load reveal (Phase 6.10) ─────────────────────────────
  // Animates headline chars + sub + CTAs in once on first paint.
  // The scroll-pinned fade-out moved to initCinematicIntro() because
  // the hero now shares a pinned parent (.hero-handoff) with shop-cat.
  function initHeroOnLoadReveal() {
    var hero = document.querySelector('.hero-alt');
    var headline = document.querySelector('.hero-alt__headline');
    var sub = document.querySelector('.hero-alt__sub');
    var cta1 = document.querySelector('.hero-alt__cta--primary');
    var cta2 = document.querySelector('.hero-alt__cta--secondary');
    if (!hero) return;
    if (hero.dataset.iliveHeroLoadDone === 'true') return;

    // Split headline immediately so reduced-motion + non-JS users see correct text.
    if (headline) splitChars(headline);
    var headlineChars = headline
      ? Array.prototype.slice.call(headline.querySelectorAll('.ilive-char:not(.ilive-char--space)'))
      : [];

    // Explicit set+to so chars actually animate from 0 -> 1.
    if (headlineChars.length) {
      gsap.set(headlineChars, { opacity: 0, y: 30 });
      gsap.to(headlineChars, { opacity: 1, y: 0, stagger: 0.04, duration: 0.6, ease: 'power3.out' });
    }
    if (sub)  { gsap.set(sub,  { opacity: 0, y: 20 }); gsap.to(sub,  { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', delay: 0.4 }); }
    if (cta1) { gsap.set(cta1, { opacity: 0, x: -30 }); gsap.to(cta1, { opacity: 1, x: 0, duration: 0.5, ease: 'power3.out', delay: 0.6 }); }
    if (cta2) { gsap.set(cta2, { opacity: 0, x:  30 }); gsap.to(cta2, { opacity: 1, x: 0, duration: 0.5, ease: 'power3.out', delay: 0.7 }); }

    hero.dataset.iliveHeroLoadDone = 'true';
  }

  // ── DOM rearrangement (Phase 6.10) ───────────────────────────────
  // Wraps .hero-alt and .shop-cat into a single .hero-handoff parent so
  // they can share a pinned ScrollTrigger. Idempotent — safe to call
  // multiple times (e.g. on shopify:section:load).
  function buildHeroHandoffWrapper() {
    var hero = document.querySelector('.hero-alt');
    var shopCat = document.querySelector('[data-shop-cat-overlap-target]');
    if (!hero || !shopCat) return null;
    if (hero.parentElement && hero.parentElement.classList.contains('hero-handoff')) {
      // Already wrapped. Ensure shop-cat is the second child.
      if (shopCat.parentElement !== hero.parentElement) {
        hero.parentElement.appendChild(shopCat);
      }
      return hero.parentElement;
    }
    var wrapper = document.createElement('div');
    wrapper.className = 'hero-handoff';
    hero.parentNode.insertBefore(wrapper, hero);
    wrapper.appendChild(hero);
    wrapper.appendChild(shopCat);
    return wrapper;
  }

  // ── Cinematic intro: stacked-pin sequence (Phase 6.10) ───────────
  // Hero+shop-cat pin together; shop-cat slides up over the fading hero.
  // Then era-sel pins separately with a sequential fade-in and runs the
  // platform parallax as a child tween of its own pin timeline.
  // Desktop + no-reduced-motion only. Mobile uses CSS display:contents
  // fallback so layout reverts to normal flow.
  function initCinematicIntro() {
    var wrapper = buildHeroHandoffWrapper();
    if (!wrapper) return;

    var hero = wrapper.querySelector('.hero-alt');
    var shopCat = wrapper.querySelector('[data-shop-cat-overlap-target]');
    var shopCatCards = shopCat
      ? Array.prototype.slice.call(shopCat.querySelectorAll('.shop-cat__grid > *'))
      : [];
    var headline = hero ? hero.querySelector('.hero-alt__headline') : null;
    var cta1 = hero ? hero.querySelector('.hero-alt__cta--primary') : null;
    var cta2 = hero ? hero.querySelector('.hero-alt__cta--secondary') : null;
    var eraSel = document.querySelector('.era-sel');
    var platform = document.querySelector('[data-era-platform]');
    var eraCards = eraSel
      ? Array.prototype.slice.call(eraSel.querySelectorAll('.era-sel__card'))
      : [];

    // gsap.matchMedia handles the desktop+motion gate. The cleanup
    // function is called automatically when breakpoint flips, killing
    // all ScrollTriggers registered inside the matched callback.
    var mm = gsap.matchMedia();

    mm.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', function () {
      // Suppress directional-stagger on shop-cat (Timeline A handles its reveal).
      if (shopCat) {
        // Suppress directional-stagger on shop-cat cards — Timeline A handles them.
        var grid = shopCat.querySelector('[data-stagger-dir]');
        if (grid) grid.dataset.iliveStaggerDone = 'true';
        // Phase 6.12: shop-cat now starts visible at top:65% of hero
        // (set in tokens.css). No opacity:0 init — categories are part
        // of the hero from page load.
      }

      // ── Timeline A: hero + shop-cat overlap handoff ─────────────
      // Phase 6.13: pinSpacing:false so era-sel scrolls UP over the pinned
      // hero instead of being pushed below a pin-spacer (which created the
      // visible "blackspace" gap between shop-cat and era-sel).
      var tlA = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: function () { return 'top top'; },
          end: function () { return '+=80%'; },
          pin: true,
          pinSpacing: false,
          scrub: 0.8,
          invalidateOnRefresh: true
        }
      });

      // Hero stays visible at opacity 1, blurs + drifts up slowly + desaturates.
      // Shop-cat overtakes at full scroll speed (yPercent 0 -> -100), hero
      // moves at 0.3x speed (yPercent 0 -> -30) for parallax overlap.
      // saturate target kept high (0.85) so the vivid all-eras panorama never
      // reads as black-and-white on scroll — was 0.4, too strong for this image.
      // fromTo with an EXPLICIT start filter: tweening from 'none' let GSAP
      // infer the starting saturate as 0, so the hero rendered greyscale at
      // scroll-top and faded TOWARD color — backwards. saturate identity is 1.
      tlA.fromTo(hero, {
        filter: 'blur(0px) saturate(1)'
      }, {
        filter: 'blur(16px) saturate(0.85)',
        yPercent: -30,
        scale: 1.03,
        ease: 'none'
      }, 0);

      // Headline letter-spacing exhale during fade
      if (headline) {
        tlA.fromTo(headline,
          { letterSpacing: '0.02em' },
          { letterSpacing: '0.06em', ease: 'none' },
          0
        );
      }

      // Buttons leave first (0.2 -> 0.35 of timeline)
      if (cta1 || cta2) {
        var btns = [cta1, cta2].filter(Boolean);
        tlA.to(btns, { opacity: 0, y: -4, ease: 'power1.in' }, 0.2);
      }

      // Phase 6.12: hero text block (top-left) blurs + fades + drifts up
      // as scroll progresses — categories take over visual focus.
      var heroContent = hero ? hero.querySelector('.hero-alt__content') : null;
      if (heroContent) {
        tlA.to(heroContent, {
          filter: 'blur(8px)',
          opacity: 0.3,
          y: -40,
          ease: 'none'
        }, 0);
      }

      // Phase 6.12: shop-cat starts at top:65% of hero (visible) and slides
      // up to yPercent -150 (3x the travel — rises high into the hero before
      // era-sel overtakes). Categories cover more of the hero on the way up.
      if (shopCat) {
        tlA.to(shopCat, {
          yPercent: -150,
          ease: 'power2.out',
          onComplete: function () {
            shopCat.style.willChange = '';
          }
        }, 0);
      }

      // ── Timeline B: era-sel sequential pin + parallax ───────────
      if (eraSel) {
        // Era-sel renders fully visible from the start — scrolls into frame
        // naturally via document scroll, no fade-in. Phase 6.11 change.

        var tlB = gsap.timeline({
          scrollTrigger: {
            trigger: eraSel,
            start: function () { return 'top top'; },
            end: function () { return '+=80%'; },
            pin: true,
            pinSpacing: true,
            scrub: 0.8,
            invalidateOnRefresh: true
          }
        });

        // Shop-cat blurs + continues drifting up slowly during era-sel pin.
        // Same blur pattern as hero in Timeline A; yPercent goes from -100
        // (Timeline A end state) to -130 — continues moving at slow speed.
        if (shopCat) {
          tlB.to(shopCat, {
            filter: 'blur(16px) saturate(0.4)',
            yPercent: -130,
            ease: 'none'
          }, 0);
        }

        // Platform parallax as child tween (NOT a separate ScrollTrigger).
        // Drives the --platform-y CSS variable that the .era-sel::before
        // pseudo-element reads for its background-position.
        if (platform) {
          var proxy = { y: 0 };
          tlB.to(proxy, {
            y: CONFIG.parallax.eraPlatformY,
            ease: 'none',
            onUpdate: function () {
              platform.style.setProperty('--platform-y', proxy.y + 'px');
            }
          }, 0.3);
        }

        // Era cards subtle opposite-direction depth
        if (eraCards.length) {
          tlB.to(eraCards, { yPercent: 4, ease: 'none' }, 0.3);
        }
      }

      // Cleanup function — runs when breakpoint exits (e.g. resize to mobile)
      return function () {
        if (shopCat) { shopCat.style.willChange = ''; gsap.set(shopCat, { clearProps: 'all' }); }
        shopCatCards.forEach(function (card) { gsap.set(card, { clearProps: 'all' }); });
        if (hero) gsap.set(hero, { clearProps: 'all' });
        var heroContentEl = hero ? hero.querySelector('.hero-alt__content') : null;
        if (heroContentEl) gsap.set(heroContentEl, { clearProps: 'all' });
        if (headline) gsap.set(headline, { clearProps: 'letterSpacing' });
        if (cta1) gsap.set(cta1, { clearProps: 'opacity,y' });
        if (cta2) gsap.set(cta2, { clearProps: 'opacity,y' });
        eraCards.forEach(function (card) { gsap.set(card, { clearProps: 'yPercent' }); });
        if (platform) platform.style.removeProperty('--platform-y');
      };
    });
  }

  // ── Featured Chapter scroll-in reveal (Phase 6.6 hotfix: no pin) ─
  // Storytelling: eyebrow -> headline -> sub -> pull-quote (char) -> 3 cards
  // from L/C/R -> CTA. Plays once when the section enters the viewport at
  // both desktop and mobile. The old pinned/scrubbed desktop timeline was
  // removed because pinSpacing added ~900px of empty scroll below the
  // section, leaving a big blank cream void between feat-chap and feat-prod.
  function initFeatChapTimeline() {
    var sec = document.querySelector('.feat-chap');
    if (!sec) return;

    var eyebrow = sec.querySelector('.feat-chap__eyebrow');
    var headline = sec.querySelector('.feat-chap__headline');
    var sub = sec.querySelector('.feat-chap__subtitle');
    var quote = sec.querySelector('.feat-chap__quote');
    var cards = Array.prototype.slice.call(sec.querySelectorAll('.feat-chap__card'));
    var cta = sec.querySelector('.feat-chap__cta');

    if (headline) splitChars(headline);
    var quoteP = quote ? quote.querySelector('p') : null;
    if (quoteP) splitChars(quoteP);

    var headlineChars = headline ? Array.prototype.slice.call(headline.querySelectorAll('.ilive-char:not(.ilive-char--space)')) : [];
    var quoteChars = quoteP ? Array.prototype.slice.call(quoteP.querySelectorAll('.ilive-char:not(.ilive-char--space)')) : [];

    // One timeline, fires when section enters viewport. No pinning.
    var tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      scrollTrigger: {
        trigger: sec,
        start: 'top 80%',
        once: true
      }
    });

    if (eyebrow) tl.from(eyebrow, { opacity: 0, y: 12, duration: 0.4 }, 0);
    if (headlineChars.length) tl.from(headlineChars, { opacity: 0, y: 30, stagger: 0.03, duration: 0.5 }, 0.15);
    if (sub) tl.from(sub, { opacity: 0, y: 12, duration: 0.4 }, '-=0.2');
    if (quoteChars.length) tl.from(quoteChars, { opacity: 0, y: 20, stagger: CONFIG.featChap.quoteCharStaggerMs / 1000, duration: 0.5 }, '-=0.1');
    if (cards.length) {
      cards.forEach(function (card, i) {
        var fromX = 0, fromY = 0;
        if (i === 0) fromX = -80;
        else if (i === cards.length - 1) fromX = 80;
        else fromY = 50;
        tl.from(card, { opacity: 0, x: fromX, y: fromY, duration: 0.5 }, '<' + (i * 0.08));
      });
    }
    if (cta) tl.from(cta, { opacity: 0, scale: 0.95, duration: 0.4 }, '-=0.2');
  }

  // ── Parallax layers ──────────────────────────────────────────────
  // Declarative: any element with [data-ilive-parallax-bg] gets a y-translate
  // tied to scroll. era-platform uses a CSS variable on its parent so the
  // ::before pseudo can pick it up (since GSAP can't tween pseudo-elements).
  // Idempotent via [data-ilive-parallax-done] / [data-ilive-platform-done].
  function initParallaxLayers() {
    var bgTargets = Array.prototype.slice.call(document.querySelectorAll('[data-ilive-parallax-bg]'));
    bgTargets.forEach(function (el) {
      if (el.dataset.iliveParallaxDone === 'true') return;
      gsap.to(el, {
        y: CONFIG.parallax.archiveTeaserBgY,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
      el.dataset.iliveParallaxDone = 'true';
    });

    // Era-sel platform parallax migrated to initCinematicIntro() (Phase 6.10).
    // It now runs as a child tween of the era-sel pin timeline, so it freezes
    // when the pin releases. Two ScrollTriggers on a pinned element glitch
    // (gsap-scrolltrigger specialist warning).
  }

  // ── Magnetic cursor attraction ───────────────────────────────────
  // Elements with [data-ilive-magnetic] translate toward the cursor when
  // it's within hitBoxPx. Per-element pull max via [data-magnetic-strength]
  // (defaults to CONFIG.magnetic.ctaMaxPx). Hover-capable devices only.
  function initMagneticElements() {
    if (!(window.matchMedia && window.matchMedia('(hover: hover)').matches)) return;

    var targets = Array.prototype.slice.call(document.querySelectorAll('[data-ilive-magnetic]'));
    targets.forEach(function (el) {
      // Idempotency guard: skip if already wired (re-runs on shopify:section:load)
      if (el.dataset.iliveMagneticDone === 'true') return;

      var strength = parseFloat(el.dataset.magneticStrength) || CONFIG.magnetic.ctaMaxPx;
      var rect = null;

      function onMove(e) {
        if (!rect) rect = el.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var dist = Math.sqrt(dx * dx + dy * dy);
        var maxDist = Math.max(rect.width, rect.height) / 2 + CONFIG.magnetic.hitBoxPx;
        if (dist > maxDist) {
          release();
          return;
        }
        var pull = Math.min(strength, dist * 0.3);
        var nx = (dx / dist) * pull;
        var ny = (dy / dist) * pull;
        // gsap.to has its own rAF batching — no need to wrap in our own
        gsap.to(el, { x: nx, y: ny, duration: CONFIG.magnetic.tweenDuration, ease: CONFIG.magnetic.tweenEase, overwrite: 'auto' });
      }

      function release() {
        rect = null;
        gsap.to(el, { x: 0, y: 0, duration: CONFIG.magnetic.tweenDuration, ease: CONFIG.magnetic.tweenEase, overwrite: 'auto' });
      }

      function onEnter() { rect = el.getBoundingClientRect(); }
      function onLeave() { release(); }

      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseleave', onLeave);
      el.dataset.iliveMagneticDone = 'true';
    });
  }

  // ── Directional stagger reveals ──────────────────────────────────
  // Grids with [data-stagger-dir] reveal their children in a chosen order:
  //   ltr  = left-to-right (forloop index)
  //   rtl  = right-to-left (reverse index)
  //   center = center-out (cards near middle index reveal first)
  function initDirectionalStagger() {
    var grids = Array.prototype.slice.call(document.querySelectorAll('[data-stagger-dir]'));
    grids.forEach(function (grid) {
      if (grid.dataset.iliveStaggerDone === 'true') return;
      var dir = grid.dataset.staggerDir || 'ltr';
      var children = Array.prototype.slice.call(grid.children);
      if (!children.length) return;

      var order;
      if (dir === 'rtl') {
        order = children.map(function (_, i) { return i; }).reverse();
      } else if (dir === 'center') {
        var mid = Math.floor((children.length - 1) / 2);
        order = [];
        for (var off = 0; off <= Math.max(mid, children.length - 1 - mid); off++) {
          if (mid - off >= 0) order.push(mid - off);
          if (off !== 0 && mid + off < children.length) order.push(mid + off);
        }
      } else {
        order = children.map(function (_, i) { return i; });
      }

      // Set FROM state immediately so cards don't flash visible before tween
      children.forEach(function(el) { gsap.set(el, { opacity: 0, y: CONFIG.reveal.y }); });

      order.forEach(function (childIdx, revealIdx) {
        var el = children[childIdx];
        if (!el) return;
        gsap.to(el, {
          opacity: 1,
          y: 0,
          duration: CONFIG.reveal.duration,
          ease: CONFIG.reveal.ease,
          delay: revealIdx * CONFIG.reveal.stagger,
          scrollTrigger: {
            trigger: grid,
            start: 'top 90%',
            once: true
          }
        });
      });
      grid.dataset.iliveStaggerDone = 'true';
    });
  }

  // ── Breathing center card ────────────────────────────────────────
  // Subtle infinite floating animation on whichever era card has
  // [data-centered="true"]. Re-attaches on era:change events from
  // era-carousel.js. Hover-capable devices only (battery courtesy).
  var _breathingBound = false;
  var _breathingTween = null;
  function initBreathingCenterCard() {
    if (!(window.matchMedia && window.matchMedia('(hover: hover)').matches)) return;
    if (window.matchMedia('(max-width: 639px)').matches) return; // skip on mobile (no carousel)

    function attach() {
      if (_breathingTween) _breathingTween.kill();
      var centered = document.querySelector('.era-sel__card[data-centered="true"]');
      if (!centered) return;
      _breathingTween = gsap.to(centered, {
        y: -CONFIG.breathing.distance,
        duration: CONFIG.breathing.cycleSec / 2,
        ease: 'power1.inOut',
        yoyo: true,
        repeat: -1
      });
    }

    // Initial attach (after first carousel render, which sets data-centered)
    setTimeout(attach, 100);

    // Bind the era:change listener once across the page lifetime
    if (!_breathingBound) {
      document.addEventListener('era:change', attach);
      _breathingBound = true;
    }
  }

  // ── Clip-path reveals ────────────────────────────────────────────
  // Element with [data-ilive-clip-reveal] reveals via inset clip-path
  // animation. Once on scroll-into-view.
  function initClipReveals() {
    var targets = Array.prototype.slice.call(document.querySelectorAll('[data-ilive-clip-reveal]'));
    targets.forEach(function (el) {
      if (el.dataset.iliveClipDone === 'true') return;
      gsap.fromTo(el,
        { clipPath: 'inset(0 100% 0 0)' },
        {
          clipPath: 'inset(0 0% 0 0)',
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 90%',
            once: true
          }
        }
      );
      el.dataset.iliveClipDone = 'true';
    });
  }

  function initSectionReveals() {
    REVEAL_SPECS.forEach(function (spec) {
      revealOnScroll(spec.selector, { trigger: spec.trigger, stagger: spec.stagger });
    });
  }

  function initRevealsInside(root) {
    // Run reveals only for specs whose trigger lives inside the swapped section
    REVEAL_SPECS.forEach(function (spec) {
      var trigger = root.querySelector(spec.trigger);
      if (!trigger) return;
      revealOnScroll(spec.selector, { trigger: trigger, stagger: spec.stagger, root: root });
    });
  }

  function revealOnScroll(selector, opts) {
    opts = opts || {};
    // Resolve elements: scoped to root if provided (for shopify:section:load)
    var els = opts.root
      ? Array.prototype.slice.call(opts.root.querySelectorAll(selector))
      : Array.prototype.slice.call(document.querySelectorAll(selector));
    if (!els.length) return;

    gsap.set(els, { opacity: 0, y: CONFIG.reveal.y });
    gsap.to(els, {
      opacity: 1,
      y: 0,
      duration: CONFIG.reveal.duration,
      ease: CONFIG.reveal.ease,
      stagger: opts.stagger || 0,
      scrollTrigger: {
        trigger: opts.trigger || els[0],
        start: 'top 90%',
        once: true
      }
    });
  }
})();
